import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useToast } from "../../modules/layout/components/ToastContext";
import Loader from "../../modules/layout/components/Loader";
import { getAll, post, remove } from "../_request";
import AppBreadcrumb from "../../modules/layout/components/AppBreadcrumb";
import { useAuth, userHasRole } from "../../../setup/auth/authService";
import { AdminWeb, ManageRoles } from "../../constants/constants";
import { IProfileDetailsDto } from "../../interfaces/IUtilities";

interface UserRole {
  Name: string;
  Assigned: boolean;
}

export default function AssignRole() {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const navigate = useNavigate();

  const { targetProfileId } = useParams<{ targetProfileId: string }>();
  const [profile, setProfile] = useState<IProfileDetailsDto | null>(null);
  const [roles, setRoles] = useState<UserRole[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { user } = useAuth();
  const [isAdminWeb, setIsAdminWeb] = useState(false);
  const [isManageRoles, setIsManageRoles] = useState(false);
        
  useEffect(() => {
    const checkUserRole = async () => {
      const hasAdminWebRole = await userHasRole(AdminWeb);
      const hasManageRolesRole = await userHasRole(ManageRoles);
      setIsAdminWeb(hasAdminWebRole);
      setIsManageRoles(hasManageRolesRole);
    };  
    if (user && !user.expired) {
      checkUserRole();
    }
  }, [user]);

  useEffect(() => {
    if (!targetProfileId) {
      showToast && showToast(t("NoProfileIdProvided"), "warning");
      return;
    }

    const fetchRoles = async () => {
      setLoading(true);
      setError(null);
      try {
        const userResult: any = await getAll(`/api/UserEvents/getprofiledetails`,`?targetProfileId=${encodeURIComponent(targetProfileId)}`);
        
        if (userResult?.data?.Data) {
          setProfile(userResult.data.Data);
        }
        const result: any = await getAll(
          `/api/UserEvents/GetUserRoles`,`?targetProfileId=${encodeURIComponent(targetProfileId)}`
        );

        const respStatus = result?.response?.status || result?.status;
        if (respStatus === 200 || respStatus === undefined) {
          let payload: UserRole[] = result?.data?.Data ?? [];
          payload = payload.filter(r => r.Name !== "GG_AdminWeb");
          setRoles(payload);

        } else if (respStatus === 403) {
          navigate("/error/403");
        } else if (respStatus === 401) {
          navigate("/auth/login");
        } else if (respStatus === 404) {
          showToast && showToast(t("ProfileNotFound"), "error");
          navigate("/manage-accounts");
        } else if (respStatus === 500) {
          navigate("/error/500");
        } else {
          showToast && showToast(t("StatusCode", { code: String(respStatus) }), "error");
        }
      } catch (err: any) {
        console.error(err);
        setError(String(err?.message ?? err));
        showToast && showToast(t("ConnectionError", { error: String(err) }), "error");
      } finally {
        setLoading(false);
      }
    };

    fetchRoles();
  }, [targetProfileId]);

  const roleConfig: Record<string, { label: string; description: string }> = {
    GG_Transactions_Approve: {
      label: t("TransactionsApprove"),
      description: t("TransactionsApproveDescription")
    },
    GG_AuditLogs_Viewer: {
      label: t("AuditLogsViewer"),
      description: t("AuditLogsViewerDescription")
    },
    GG_GatewayLogs_Viewer: {
      label: t("GatewayLogsViewer"),
      description: t("GatewayLogsViewerDescription")
    },
    GG_HelpDeskOperator: {
      label: t("HelpDeskOperator"),
      description: t("HelpDeskOperatorDescription")
    },
    GG_ManageProfile: {
      label: t("ManageProfile"),
      description: t("ManageProfileDescription")
    },
    GG_SSO_Clients: {
      label: t("SSOClients"),
      description: t("ClientsDescription")
    },
    GG_Manage_Services: {
      label: t("ManageServices"),
      description: t("ManageServicesDescription")
    },
    GG_Manage_Institutions: {
      label: t("ManageInstitutions"),
      description: t("ManageInstitutionsDescription")
    },
    GG_Claims_Viewer: {
      label: t("ClaimsViewer"),
      description: t("ClaimsViewerDescription")
    },
    GG_Manage_Template_Claims: {
      label: t("ManageTemplateClaims"),
      description: t("ManageTemplateClaimsDescription")
    },
    GG_Manage_Roles: {
      label: t('ManageRoles'),
      description: t('ManageRolesDescription')
    }
  };

  const toggleRole = (roleName: string) => {
    setRoles((prev) =>
      prev.map((r) => (r.Name === roleName ? { ...r, Assigned: !r.Assigned } : r))
    );
  };

  const saveChanges = async () => {
    if (!targetProfileId) return;
    setSaving(true);
    try {
      const rolesToAssign = roles.filter((r) => r.Assigned).map((r) => r.Name);
      const rolesToUnassign = roles.filter((r) => !r.Assigned).map((r) => r.Name);

      if (rolesToAssign.length > 0) {
        await post(
          `/api/userevents/assignuserroles?targetProfileId=${encodeURIComponent(targetProfileId)}`,
          rolesToAssign
        );
      }

      if (rolesToUnassign.length > 0) {
        await remove(`/api/userevents/unassignuserroles`, {
          params: { targetProfileId },
          data: rolesToUnassign,
        });
      }

      showToast && showToast(t("RolesUpdatedSuccessfully"), "success");
    } catch (err: any) {
      console.error("Save Error:", err);
      showToast && showToast(t("FailedToSaveRoles"), "error");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Loader />;
  if (error) return <div className="alert alert-danger">{error}</div>;
  if (!roles.length) return <p>{t("NoRolesFound")}</p>;

  return (
    <div className="mt-4 ms-3 gg-serviceList">
      <AppBreadcrumb
            items={[
              { title: t('Home'), href: '/' },
              { title: t('ManageAccounts'), href:'/manage-accounts'},
              { title: t('UserRoles')}
            ]}
      />
      <h3 className="mb-3">{t("UserRoles")}</h3>
      {profile && <h4 className="fw-bold mb-4 mt-4 ">{profile.Username}</h4>}
      
      <div className="gg-roles-container">
        <div className="table-responsive border rounded bg-white me-2">
          
          <table className="table table-hover m-0">
            <thead>
              <tr>
                <th style={{ width: "60px" }} className="text-center">
                  {t('Assign')}
                </th>               
                <th style={{ width: "30%" }}>{t("Role")}</th>
                <th style={{ width: "70%" }}>{t("Description")}</th>
              </tr>
            </thead>
            <tbody>
              {roles.map((role) => (
                <tr 
                    key={role.Name} 
                    onClick={() => {
                      if (isAdminWeb && isManageRoles) {
                        toggleRole(role.Name);
                      }}}
                    className={`gg-role-row ${(isAdminWeb && isManageRoles) ? "cursor-pointer" : ""}`}
                >

                    <td className="text-center" onClick={(e) => e.stopPropagation()}>
                      <input className="form-check-input"
                             type="checkbox"
                             checked={role.Assigned}
                             disabled={!(isAdminWeb && isManageRoles)}
                             onChange={() => {if (isAdminWeb && isManageRoles) toggleRole(role.Name)}} 
                       />
                    </td>
                  
                    <td className="align-middle">
                      <span style={{ fontWeight: 500 }}>
                        {roleConfig[role.Name]?.label ?? role.Name}
                      </span>
                    </td>
                    <td className="align-middle text-muted small">
                      {roleConfig[role.Name]?.description ?? "-"}
                    </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {(isAdminWeb && isManageRoles) && (
          <div className="mt-3">
           <button
             className="gg-btn gg-btn-submit "
             onClick={saveChanges}
             disabled={saving}
            >
             {saving ? t("Saving") + "..." : t("SaveChanges")}
           </button>
         </div>
       )}
      </div>
    </div>
  );
};
